import { useCallback, useEffect, useRef, useState } from "react";

// How often the countdown redraws. One second, because that is the unit the
// child is being shown and the unit the server grades in — a finer tick would
// animate a number that never changes.
const TICK_MS = 1000;

export type QuestionClock = {
  /** Seconds this question allows, or null when it is not a timed question. */
  limit: number | null;
  /** Seconds left, floored at 0. null when nothing is being timed. */
  remaining: number | null;
  /** Whole seconds spent so far, which is what the server is told. */
  elapsed: number;
  /** The limit was reached. Nothing locks — this only changes what is shown. */
  expired: boolean;
  /** Timing was abandoned for this question: it asked for help, or it is done. */
  voided: boolean;
  /** Stop the clock and keep the elapsed reading (called when answering). */
  stop: () => void;
  /** Abandon timing entirely — no reading is reported and nothing counts late. */
  voidClock: () => void;
};

/**
 * Counts how long the learner has been looking at ONE question, for the small
 * minority of questions where answering fast is part of the skill.
 *
 * Deliberately separate from `useSessionTimer`, which measures something else
 * and cannot be reused here: that one is idle-aware, flushes every 15 seconds
 * and keeps accruing while the theory panel and the tutor are open. Perfect for
 * "what did she spend the session on", and far too coarse and too generous to
 * put a countdown on.
 *
 * The clock does not run while the tab is hidden. A child who switched away has
 * not been staring at `7 × 8` — and the alternative punishes a interruption
 * nobody chose.
 *
 * It does NOT pause for idleness, unlike the session timer. Sitting and staring
 * IS the thing being measured here: a fluency limit that stops whenever the
 * child stops moving would never run out for the child it exists to detect.
 *
 * @param limit    seconds the question allows, or null/undefined for untimed
 * @param active   whether this question is the one on screen
 * @param settled  whether it has been answered or skipped already
 */
export function useQuestionClock(
  limit: number | null | undefined,
  active: boolean,
  settled: boolean
): QuestionClock {
  const [elapsed, setElapsed] = useState(0);
  const [voided, setVoided] = useState(false);
  const [running, setRunning] = useState(false);

  // Whole seconds accrued, kept in a ref so the interval never reads a stale
  // count between renders.
  const elapsedRef = useRef(0);
  const timed = typeof limit === "number" && limit > 0;

  // A question that is not being timed reports nothing, so a reading can never
  // be attributed to the wrong question.
  const stop = useCallback(() => setRunning(false), []);
  const voidClock = useCallback(() => {
    setRunning(false);
    setVoided(true);
  }, []);

  // Restart whenever the question changes. `limit` alone is not enough of a key
  // — two consecutive questions of the same skill share it, and the clock would
  // carry the first one's seconds into the second.
  useEffect(() => {
    elapsedRef.current = 0;
    setElapsed(0);
    setVoided(false);
    setRunning(timed && active && !settled);
  }, [timed, active, settled]);

  useEffect(() => {
    if (!running) return;

    const tick = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      elapsedRef.current += TICK_MS / 1000;
      setElapsed(elapsedRef.current);
    }, TICK_MS);

    return () => window.clearInterval(tick);
  }, [running]);

  const showing = timed && !voided;
  const remaining = showing ? Math.max(0, (limit as number) - elapsed) : null;

  return {
    limit: timed ? (limit as number) : null,
    remaining,
    elapsed,
    // Reported off the elapsed count rather than off `remaining`, which is null
    // once voided — and a voided clock has not expired, it has been called off.
    expired: showing && elapsed >= (limit as number),
    voided,
    stop,
    voidClock,
  };
}
